import {useState, useCallback, type MouseEventHandler, type MouseEvent} from 'react';
import {FormattedMessage} from 'react-intl';
import {useMutation, type ApolloCache, type Reference} from '@apollo/client';
import {DELETE_BOOKMARK_FOLDER_MUTATION, type DeleteBookmarkFolderOutput} from 'graphql/admin/bookmarks';
import {DocumentTypename} from 'types';
import {useSnackbar} from 'hooks';
import {getErrorMessage} from 'utils';
import {useSelectedLinkContext} from 'contexts/SelectedLinkContext';
import {DeleteIconButton, type DeleteIconButtonProps} from 'components/buttons';
import DeleteBookmarkFolderDialog from './DeleteBookmarkFolderDialog';

const updateFoldersCache = (deletedFolderKey : string) => (cache: ApolloCache<any>, {data} : {data: DeleteBookmarkFolderOutput}) => {
  const isDeleted = data?.deleteFolder?.isDeleted;
  if (isDeleted) cache.modify({
    fields: {
      adminFoldersByFolderType({folders: existingFoldersRefs = [], ...adminFoldersByFolderType}) {
        const deletedFolderCacheRef = cache.identify({__typename: DocumentTypename, key: deletedFolderKey});
        return {
          ...adminFoldersByFolderType,
          folders: existingFoldersRefs.filter((folderRef : Reference) => folderRef.__ref !== deletedFolderCacheRef)
        }
      }
    }
  });
};

type DeleteBookmarkFolderIconButtonProps = DeleteIconButtonProps & {
  folderName: string;
  folderKey: string;
}

const DeleteBookmarkFolderIconButton = ({folderName, folderKey} : DeleteBookmarkFolderIconButtonProps) : JSX.Element => {
  const [open, setOpen] = useState(false);

  const {selectedLink: {channelKey: channel}} = useSelectedLinkContext();
  const {successSnackbar} = useSnackbar();

  const onCompleted = (data : DeleteBookmarkFolderOutput) => {
    const isDeleted = data?.deleteFolder?.isDeleted;
    if (isDeleted) {
      successSnackbar(<FormattedMessage id="admin.bookmarks.folder.delete.success" values={{ folderName }} />);
    }
  }

  const [deleteFolder, {loading, data, error, reset}] = useMutation<DeleteBookmarkFolderOutput>(DELETE_BOOKMARK_FOLDER_MUTATION, { update: updateFoldersCache(folderKey), onCompleted });

  const handleClick : MouseEventHandler<HTMLButtonElement> = () => setOpen(true);

  const handleClose = useCallback(() => {
    setOpen(false);
    reset();
  }, [reset]);

  const handleDeleteFolder = async (event : MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    await deleteFolder({variables: {deleteFolderInput: {channel, folderKey}}});
  }

  return <>
    <DeleteIconButton
      labelId='admin.bookmarks.button.deleteFolder'
      className='bg-admin-red-400 hover:bg-admin-red-500'
      onClick={handleClick}
    />
    <DeleteBookmarkFolderDialog
      errorReason={data?.deleteFolder?.errorReason || getErrorMessage(error)}
      folderName={folderName}
      open={open}
      onClose={handleClose}
      submitting={loading}
      handleDeleteFolder={handleDeleteFolder}
    />
  </>
}

export default DeleteBookmarkFolderIconButton;